import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { handleResponse } from 'actions/actionHelpers';
import { useParams } from 'react-router';
import { getConversation, updateConversation, createMessage } from 'actions/admin/conversations';
import { fetchPredefinedTexts } from 'actions/predefinedTexts';
import { convertToNumber, shorten } from 'helpers/StringHelper';
import routes from 'routes';
import conversationCategories from 'enums/conversationCategories';
import conversationStatuses from 'enums/conversationStatuses';

import Loader from 'common/Loader';
import AdminPage from 'admin/presentational/AdminPage';
import AdminContentWrapper from 'admin/presentational/AdminContentWrapper';
import LinkButton from 'common/LinkButton';
import SendMessageForm from './SendMessageForm';
import MessageList from './MessageList';
import AdminFormWrapper from 'admin/presentational/AdminFormWrapper';
import AdminFormFields from 'admin/presentational/AdminFormFields';
import ReadOnlyField from 'admin/presentational/ReadOnlyField';
import ChangeStatusDialog from './ChangeStatusDialog';
import { Link } from 'react-router-dom';

const Conversation = () => {
    const params = useParams();
    const dispatch = useDispatch();
    const history = useHistory();
    const authenticationContext = useSelector(state => state.authentication.context);
    const { user } = authenticationContext;

    const [conversation, setConversation] = useState(undefined);
    const [predefinedTexts, setPredefinedTexts] = useState(undefined);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [changeStatusDialogOpen, setChangeStatusDialogOpen] = useState(false);

    useEffect(() => {
        loadConversation();
        loadPredefinedTexts();
    }, []);

    const loadConversation = () => {
        dispatch(getConversation(params.conversationId))
            .then(handleResponse(
                response => {
                    setConversation(response.payload);
                },
                () => setIsLoading(false)
            ));
    };

    const loadPredefinedTexts = () => {
        dispatch(fetchPredefinedTexts('backofficeConversations'))
            .then(handleResponse(
                response => {
                    setPredefinedTexts(response.payload);
                },
                () => setIsLoading(false)
            ));
    };

    useEffect(() => {
        if(conversation && predefinedTexts) {
            setIsLoading(false);
        }
    }, [conversation, predefinedTexts]);

    const handleChangeStatusClick = () => {
        setChangeStatusDialogOpen(true);
    };

    const handleChangeStatusDialogSubmit = formValues => {
        setChangeStatusDialogOpen(false);
        setIsSubmitting(true);
        const updateConversationRequest = {
            status: formValues.status
        };
        dispatch(updateConversation(conversation.id, updateConversationRequest))
            .then(handleResponse(
                () => {
                    history.push(routes.admin.conversations);
                    setIsSubmitting(false);
                    return { snackbar: { message: 'Conversation was successfully updated.' } };
                },
                () => setIsSubmitting(false)
            ));
    };

    const handleChangeStatusDialogCancel = () => {
        setChangeStatusDialogOpen(false);
    };

    const handleSendMessageFormSubmit = formValues => {
        const createMessageRequest = {
            senderUserId: user.id,
            senderActorId: convertToNumber(formValues.senderActorId),
            sendAsAdmin: formValues.sendAsAdmin,
            body: formValues.body,
            category: conversationCategories.general.key,
            status: formValues.status,
            sendSms: formValues.sendSms
        };
        setIsSubmitting(true);
        dispatch(createMessage(conversation.id, createMessageRequest))
            .then(handleResponse(
                () => {
                    history.push(routes.admin.conversations);
                    setIsSubmitting(false);
                    return { snackbar: { message: 'Message was successfully sent.' } };
                },
                () => setIsSubmitting(false)
            ));
    };

    const title = 'Conversation: ' + (conversation ? shorten(conversation.storageSiteTitle, 40) : 'Loading...');

    return (
        <AdminPage title={title}>
            {
                isLoading && <Loader />
            }
            {
                conversation && predefinedTexts &&
                (
                    <AdminContentWrapper type="list">
                        <AdminFormWrapper isSubmitting={isSubmitting}>
                            <AdminFormFields>
                                <ReadOnlyField label="Storage site">
                                    <Link to={routes.storageSiteDetails.replace(':storageSiteId', conversation.storageSiteId)} target="_blank">{conversation.storageSiteTitle}</Link>
                                </ReadOnlyField>

                                <ReadOnlyField label="Category">
                                    {conversation.category}
                                </ReadOnlyField>

                                <ReadOnlyField label="Status">
                                    {conversationStatuses[conversation.status].title} | <LinkButton onClick={handleChangeStatusClick}>Change</LinkButton>
                                </ReadOnlyField>

                                <ChangeStatusDialog
                                    open={changeStatusDialogOpen}
                                    onSubmit={handleChangeStatusDialogSubmit}
                                    onCancel={handleChangeStatusDialogCancel}
                                    conversation={conversation}
                                />
                            </AdminFormFields>
                        </AdminFormWrapper>

                        <AdminFormWrapper isSubmitting={isSubmitting}>
                            <SendMessageForm
                                conversation={conversation}
                                predefinedTexts={predefinedTexts}
                                onSubmit={handleSendMessageFormSubmit}
                            />
                        </AdminFormWrapper>

                        <AdminFormWrapper>
                            <AdminFormFields>
                                <ReadOnlyField label="Messages">
                                    <MessageList messages={conversation.messages} />
                                </ReadOnlyField>
                            </AdminFormFields>
                        </AdminFormWrapper>
                    </AdminContentWrapper>
                )
            }
        </AdminPage>
    );
};

export default Conversation;
